/**
 * Convert a Schema.org JSON-LD object into Microdata or RDFa HTML.
 *
 * Microdata example:
 *   <div itemscope itemtype="https://schema.org/Recipe">
 *     <span itemprop="name">Apple Pie</span>
 *     <span itemprop="author" itemscope itemtype="https://schema.org/Person">
 *       <span itemprop="name">Jane Smith</span>
 *     </span>
 *   </div>
 *
 * RDFa example:
 *   <div vocab="https://schema.org/" typeof="Recipe">
 *     <span property="name">Apple Pie</span>
 *     <span property="author" typeof="Person">
 *       <span property="name">Jane Smith</span>
 *     </span>
 *   </div>
 *
 * The output is a self-contained HTML fragment the user can paste into
 * their template's <body>. Both formats are render-safe (won't display
 * any markup the user can see) — they're attribute-only annotations on
 * existing elements.
 */

type JsonObject = Record<string, unknown>;

function isObject(v: unknown): v is JsonObject {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function indent(level: number): string {
  return '  '.repeat(level);
}

// ---------------------------------------------------------------------------
// JSON-LD: pretty-print + wrap in <script>
// ---------------------------------------------------------------------------

export function toJsonLdString(obj: JsonObject, opts: { script?: boolean } = {}): string {
  const json = JSON.stringify(obj, null, 2);
  if (opts.script) {
    return `<script type="application/ld+json">\n${json}\n</script>`;
  }
  return json;
}

// ---------------------------------------------------------------------------
// Microdata
// ---------------------------------------------------------------------------

export function toMicrodata(obj: JsonObject): string {
  const lines: string[] = [];
  renderMicrodata(obj, 0, lines, /* asRoot */ true);
  return lines.join('\n');
}

function renderMicrodata(
  obj: JsonObject,
  level: number,
  out: string[],
  asRoot: boolean
): void {
  const type = typeof obj['@type'] === 'string' ? (obj['@type'] as string) : null;
  const id = typeof obj['@id'] === 'string' ? (obj['@id'] as string) : null;
  const attrs: string[] = ['itemscope'];
  if (type) attrs.push(`itemtype="https://schema.org/${type}"`);
  if (id) attrs.push(`itemid="${escapeHtml(id)}"`);

  out.push(`${indent(level)}<div ${attrs.join(' ')}>`);
  for (const [key, value] of Object.entries(obj)) {
    if (key.startsWith('@')) continue;
    renderMicrodataProperty(key, value, level + 1, out);
  }
  out.push(`${indent(level)}</div>`);
}

function renderMicrodataProperty(
  prop: string,
  value: unknown,
  level: number,
  out: string[]
): void {
  if (value === null || value === undefined) return;

  if (Array.isArray(value)) {
    for (const item of value) renderMicrodataProperty(prop, item, level, out);
    return;
  }

  if (isObject(value)) {
    const type = typeof value['@type'] === 'string' ? (value['@type'] as string) : null;
    const id = typeof value['@id'] === 'string' ? (value['@id'] as string) : null;
    const attrs: string[] = [`itemprop="${escapeHtml(prop)}"`, 'itemscope'];
    if (type) attrs.push(`itemtype="https://schema.org/${type}"`);
    if (id) attrs.push(`itemid="${escapeHtml(id)}"`);
    out.push(`${indent(level)}<div ${attrs.join(' ')}>`);
    for (const [k, v] of Object.entries(value)) {
      if (k.startsWith('@')) continue;
      renderMicrodataProperty(k, v, level + 1, out);
    }
    out.push(`${indent(level)}</div>`);
    return;
  }

  // Scalar value
  const stringValue = String(value);
  // URL-shaped properties → use <link href> for cleaner output
  if (isUrlField(prop) && /^https?:\/\//.test(stringValue)) {
    out.push(
      `${indent(level)}<link itemprop="${escapeHtml(prop)}" href="${escapeHtml(stringValue)}">`
    );
    return;
  }
  // Date-shaped properties → <meta content> with datetime
  if (isDateField(prop)) {
    out.push(
      `${indent(level)}<meta itemprop="${escapeHtml(prop)}" content="${escapeHtml(stringValue)}">`
    );
    return;
  }
  // Default: <span>
  out.push(
    `${indent(level)}<span itemprop="${escapeHtml(prop)}">${escapeHtml(stringValue)}</span>`
  );
}

// ---------------------------------------------------------------------------
// RDFa
// ---------------------------------------------------------------------------

export function toRdfa(obj: JsonObject): string {
  const lines: string[] = [];
  renderRdfa(obj, 0, lines, /* asRoot */ true);
  return lines.join('\n');
}

function renderRdfa(obj: JsonObject, level: number, out: string[], asRoot: boolean): void {
  const type = typeof obj['@type'] === 'string' ? (obj['@type'] as string) : null;
  const id = typeof obj['@id'] === 'string' ? (obj['@id'] as string) : null;
  const attrs: string[] = [];
  if (asRoot) attrs.push('vocab="https://schema.org/"');
  if (type) attrs.push(`typeof="${escapeHtml(type)}"`);
  if (id) attrs.push(`resource="${escapeHtml(id)}"`);

  out.push(`${indent(level)}<div ${attrs.join(' ')}>`);
  for (const [key, value] of Object.entries(obj)) {
    if (key.startsWith('@')) continue;
    renderRdfaProperty(key, value, level + 1, out);
  }
  out.push(`${indent(level)}</div>`);
}

function renderRdfaProperty(
  prop: string,
  value: unknown,
  level: number,
  out: string[]
): void {
  if (value === null || value === undefined) return;

  if (Array.isArray(value)) {
    for (const item of value) renderRdfaProperty(prop, item, level, out);
    return;
  }

  if (isObject(value)) {
    const type = typeof value['@type'] === 'string' ? (value['@type'] as string) : null;
    const id = typeof value['@id'] === 'string' ? (value['@id'] as string) : null;
    const attrs: string[] = [`property="${escapeHtml(prop)}"`];
    if (type) attrs.push(`typeof="${escapeHtml(type)}"`);
    if (id) attrs.push(`resource="${escapeHtml(id)}"`);
    out.push(`${indent(level)}<div ${attrs.join(' ')}>`);
    for (const [k, v] of Object.entries(value)) {
      if (k.startsWith('@')) continue;
      renderRdfaProperty(k, v, level + 1, out);
    }
    out.push(`${indent(level)}</div>`);
    return;
  }

  const stringValue = String(value);
  if (isUrlField(prop) && /^https?:\/\//.test(stringValue)) {
    out.push(
      `${indent(level)}<a property="${escapeHtml(prop)}" href="${escapeHtml(stringValue)}">${escapeHtml(stringValue)}</a>`
    );
    return;
  }
  if (isDateField(prop)) {
    out.push(
      `${indent(level)}<meta property="${escapeHtml(prop)}" content="${escapeHtml(stringValue)}">`
    );
    return;
  }
  out.push(
    `${indent(level)}<span property="${escapeHtml(prop)}">${escapeHtml(stringValue)}</span>`
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const URL_FIELDS = new Set([
  'url', 'image', 'logo', 'sameAs', 'thumbnailUrl', 'embedUrl', 'contentUrl',
  'mainEntityOfPage', 'identifier',
]);

const DATE_FIELDS = new Set([
  'datePublished', 'dateModified', 'dateCreated', 'startDate', 'endDate',
  'uploadDate', 'datePosted', 'validThrough', 'priceValidUntil', 'birthDate',
  'foundingDate', 'expires',
]);

function isUrlField(prop: string): boolean {
  return URL_FIELDS.has(prop);
}

function isDateField(prop: string): boolean {
  return DATE_FIELDS.has(prop);
}
